import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, VehicleDto } from 'dtos'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY, Junction } from 'localConstants'
import { VehiclesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'

interface MichiganSOSBundlesManageVehiclesAddVehiclesDialogProps {
  michiganSOSBundle: MichiganSOSBundleDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
}

export default function MichiganSOSBundlesManageVehiclesAddVehiclesDialog({
  michiganSOSBundle,
  isLoading,
  open,
  onClose,
  onSave
}: MichiganSOSBundlesManageVehiclesAddVehiclesDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: true
  })

  const [vehicles, setVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehiclesParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'vin',
    michiganTitleOwnerName: michiganSOSBundle.michiganTitleOwnerName,
    titleProcessingState: 'MICHIGAN',
    michiganTitleIsApplicationPacketReadyForMISoSBundle: true,
    statuses: ['isActive', 'hasNoMichiganSOSBundle'],
    junction: Junction.AND
  })

  const getVehicles = useDebounce((parameters: VehiclesParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers
      .getVehicles(parameters)
      .then(response => {
        setVehicles(response.value)
        setTotalCount(response.totalCount ?? 0)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (!open || !getVehicles) return

    getVehicles(parameters)

    return () => {
      setVehicles([])
    }
  }, [open, getVehicles, parameters, michiganSOSBundle])

  useEffect(() => {
    setParameters({ ...parameters, michiganTitleOwnerName: michiganSOSBundle.michiganTitleOwnerName })
  }, [michiganSOSBundle])

  return (
    <Dialog maxWidth='md' open={open}>
      <Formik
        enableReinitialize
        initialValues={[] as VehicleDto[]}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({ handleSubmit, setValues, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='flex-start'>
                  <DialogTitle>Add Vehicles</DialogTitle>

                  <Grid container justifyContent='flex-start' alignItems='center' sx={{ pl: 3 }}>
                    <Grid item xs='auto' sx={{ mr: 8 }}>
                      <LabelField label='Owner' value={michiganSOSBundle.michiganTitleOwnerName} />
                    </Grid>

                    <Grid item xs='auto'>
                      <LabelField label='Count of Vehicles In This Bundle' value={michiganSOSBundle.vehicleCount?.toString() || '-'} />
                    </Grid>
                  </Grid>

                  {loadingState.isGettingVehicles && (
                    <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                      <Grid>
                        <CircularProgress color='primary' size={50} />
                      </Grid>
                    </Grid>
                  )}

                  {!loadingState.isGettingVehicles && (
                    <Grid item xs={12}>
                      <Typography variant='h6' sx={{ marginBottom: 2 }}>
                        Manage Vehicles
                      </Typography>
                      <Card variant='outlined' sx={{ width: '100%' }}>
                        <TableContainer>
                          <Table size='small'>
                            <TableHead>
                              <TableRow>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    color='primary'
                                    checked={values.length === totalCount && values.length !== 0}
                                    disabled={isLoading || values.length === 0}
                                    indeterminate={values.length > 0 && values.length !== totalCount}
                                    onChange={() => {
                                      if (values.length === totalCount) {
                                        setValues([])
                                      } else {
                                        setValues(vehicles)
                                      }
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'exporterCompanyName' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'exporterCompanyName'}
                                    direction={parameters.orderBy === 'exporterCompanyName' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'exporterCompanyName' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'exporterCompanyName'
                                      })
                                    }}
                                  >
                                    Exporter
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'buyerCompanyName' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'buyerCompanyName'}
                                    direction={parameters.orderBy === 'buyerCompanyName' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'buyerCompanyName' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'buyerCompanyName'
                                      })
                                    }}
                                  >
                                    Buyer
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'last6OfVIN' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'last6OfVIN'}
                                    direction={parameters.orderBy === 'last6OfVIN' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'last6OfVIN' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'last6OfVIN'
                                      })
                                    }}
                                  >
                                    Last 6 Of VIN
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'importerStockNumber' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'importerStockNumber'}
                                    direction={parameters.orderBy === 'importerStockNumber' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'importerStockNumber' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'importerStockNumber'
                                      })
                                    }}
                                  >
                                    Importer Stock#
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'year' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'year'}
                                    direction={parameters.orderBy === 'year' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'year' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'year'
                                      })
                                    }}
                                  >
                                    Year
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'make' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'make'}
                                    direction={parameters.orderBy === 'make' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'make' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'make'
                                      })
                                    }}
                                  >
                                    Make
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'model' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'model'}
                                    direction={parameters.orderBy === 'model' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'model' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'model'
                                      })
                                    }}
                                  >
                                    Model
                                  </TableSortLabel>
                                </TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {vehicles?.map((vehicle, index) => (
                                <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                  <TableCell sx={{ fontSize: '7.99pt' }}>
                                    <Checkbox
                                      color='primary'
                                      checked={values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)}
                                      disabled={isLoading}
                                      onChange={() => {
                                        setValues(prev =>
                                          values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)
                                            ? prev.filter(sv => sv.vehicleGuid !== vehicle.vehicleGuid)
                                            : [...prev, vehicle]
                                        )
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell>{vehicle.exporterCompany?.name || '-'}</TableCell>

                                  <TableCell>{vehicle.buyerCompany?.name || '-'}</TableCell>

                                  <TableCell>{vehicle.vin?.slice(-6)}</TableCell>

                                  <TableCell>{vehicle.importerStockNumber}</TableCell>

                                  <TableCell>{vehicle.year}</TableCell>

                                  <TableCell>{vehicle.make}</TableCell>

                                  <TableCell>{vehicle.model}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>

                            <TableFooter>
                              <TableRow>
                                <TablePagination
                                  rowsPerPageOptions={[5, 10, 25]}
                                  SelectProps={{ fullWidth: false }}
                                  count={totalCount}
                                  rowsPerPage={parameters.pageSize}
                                  page={parameters.page}
                                  onPageChange={(_, page) => {
                                    setParameters({
                                      ...parameters,
                                      page
                                    })
                                  }}
                                  onRowsPerPageChange={e => {
                                    setParameters({
                                      ...parameters,
                                      page: 0,
                                      pageSize: e.target.value as unknown as number
                                    })
                                  }}
                                />
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </TableContainer>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Owner:**

Displays the owner associated with the bundle.

This field reflects the selected Owner Name from the Owner Drop-Down and cannot be changed once vehicles are associated with the bundle.

**Count of Vehicles In This Bundle:**

Displays the total number of vehicles currently associated with the bundle.

The count is updated automatically after saving, if additional vehicles are selected in the dialog and successfully added to the bundle.

A maximum of 20 vehicles can be added to a bundle.

If a user attempts to save and the selected vehicles would result in the bundle exceeding 20 vehicles:
   - A warning message is displayed to the user.
   - The system does not save the changes.

**Add Vehicles:**

Allows users to add vehicles to the bundle using the Add Vehicles Dialog.

Lists all vehicles that meet the following criteria:
   - The Owner matches the selected owner.
   - The Title Application State is Michigan.
   - The vehicle is not already associated with an MI SoS Bundle.
   - The Application Packet Ready For MI SoS Bundle field (under Vehicle Details – Titles tab) is populated

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='secondary' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton color='primary' loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
